import type { VariantProps } from "class-variance-authority";
import { isValidElement, type ReactElement } from "react";

import { cn } from "../../utils/cn.util";
import { Avatar } from "../avatar";
import { CountryFlag } from "../country-flag";
import type { SvgIcon } from "../icons/type";
import { menuItemIconVariants } from "./styles";

export interface MenuItemAvatar {
  src?: string;
  // Its initials stand in until the image loads, or when there's none.
  name: string;
}

type MediaKey = "icon" | "avatar" | "countryCode" | "dot";
type OnlyMedia<T extends Partial<Record<MediaKey, unknown>>> = T & Partial<Record<Exclude<MediaKey, keyof T>, never>>;

/**
 * Leading media the design system has a size for, one of:
 * - `icon`: an svg icon;
 * - `avatar`: a person's picture, or their initials;
 * - `countryCode`: a country's flag, from its ISO 3166-1 alpha-2 code;
 * - `dot`: a coloured circle, such as a status's. A colour class (`bg-success-50`, `text-success-40`), or a CSS colour
 *   (`#00894F`) for one made at runtime.
 */
export type MenuItemMedia =
  | OnlyMedia<{ icon: SvgIcon }>
  | OnlyMedia<{ avatar: MenuItemAvatar }>
  | OnlyMedia<{ countryCode: string }>
  | OnlyMedia<{ dot: string }>;

/**
 * What a row shows before its label: one of the media above (`{ icon: UserIcon }`), sized by the row; or, for the
 * exceptional row none of them fits, an element, shown as given at its own size.
 */
export type MenuItemLeadingValue = MenuItemMedia | ReactElement;

export type MenuItemLeadingProps = VariantProps<typeof menuItemIconVariants> & {
  leading?: MenuItemLeadingValue;
  // A row's leading media is 20px (an avatar, 24px); a filter chip's, 16px. A dot is the same on both.
  size?: "sm" | "md";
};

/** A row's leading media at the one size the design system has for it, for a row or anything echoing the row's pick. */
export const MenuItemLeading = ({ leading, color, size = "md" }: MenuItemLeadingProps) => {
  if (leading == null) {
    return null;
  }
  if (isValidElement(leading)) {
    return <span className="flex shrink-0 items-center group-aria-disabled:opacity-50">{leading}</span>;
  }
  const { icon: Icon, avatar, countryCode, dot } = leading;
  if (Icon) {
    return <Icon className={cn(menuItemIconVariants({ color }), size === "sm" && "size-4")} />;
  }
  if (avatar) {
    return (
      <Avatar.Root size={size === "sm" ? "2xs" : "xs"} tabIndex={-1} className="group-aria-disabled:opacity-50">
        <Avatar.Image src={avatar.src} alt="" />
        <Avatar.Fallback text={avatar.name} />
      </Avatar.Root>
    );
  }
  if (countryCode) {
    return (
      <CountryFlag
        code={countryCode}
        className={cn(size === "sm" ? "size-4" : "size-6", "group-aria-disabled:opacity-50")}
      />
    );
  }
  if (dot) {
    // Unlike the other media, a dot keeps its own 10px slot, an 8px circle in it, on a row and a chip alike.
    return (
      <span className="flex size-2.5 shrink-0 items-center justify-center">
        <span
          className={cn("size-2 rounded-full bg-current group-aria-disabled:opacity-50", dot)}
          // A CSS colour paints it through `bg-current`; a class is no colour, so the browser drops it.
          style={{ color: dot }}
        />
      </span>
    );
  }
  return null;
};

MenuItemLeading.displayName = "MenuItemLeading";
